/**
 * OCC workbench store (S4.3).
 *
 * Zustand store for the 訂單控管中心 lenses — lens filter state (persisted)
 * plus the client-side acknowledge / snooze marks for the 訂單異常 worklist.
 *
 * Derive-never-restore contract: the store holds ZERO copies of any lens
 * number — rows are always re-fetched projections. The only durable facts
 * here are coordination marks (who acknowledged / snoozed which derived
 * exception row, with the mandatory ReasonRequiredModal reason):
 * - exception rows have deterministic ids (`<lineId>:<reason>`), so a mark
 *   re-attaches to the same condition across reads;
 * - rows auto-clear server-side when their rule stops firing — stale marks
 *   are pruned via `pruneExceptionMarks` whenever a fresh worklist lands;
 * - snoozes EXPIRE (`until`); there is no manual close anywhere.
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { FulfillmentNodeCode, FulfillmentOwnerRole } from '@/types/api/occ';
import type { OccExceptionReason } from '@/lib/occ/selectors';

export type OccNodeFilter = 'all' | FulfillmentNodeCode;
export type OccOwnerFilter = 'all' | FulfillmentOwnerRole;
export type OccReasonFilter = 'all' | OccExceptionReason;

/** 訂單明細 Order Lines lens filters. */
export interface OccLinesFilters {
  search: string;
  node: OccNodeFilter;
  owner: OccOwnerFilter;
  /** Show only lines currently flagged by the exception engine. */
  exceptionsOnly: boolean;
  /** 待出量 saved filter — the stat-card drill (qtyOpenToLoad > 0). */
  balanceOnly: boolean;
  /** SO/PO 對照 — grouping toggle, not a tab (ux-lens finding 5). */
  groupBySo: boolean;
}

/** 訂單異常 Exceptions lens filters. */
export interface OccExceptionFilters {
  reason: OccReasonFilter;
  /** Reveal rows currently under an active snooze (hidden by default). */
  showSnoozed: boolean;
}

export interface ExceptionSnooze {
  /** ISO expiry — the row reappears once passed (snoozes expire, never close). */
  until: string;
  reason: string;
  at: string;
}

export interface ExceptionAck {
  at: string;
  reason: string;
}

export interface OccState {
  linesFilters: OccLinesFilters;
  exceptionFilters: OccExceptionFilters;
  /** Active snoozes by derived exception row id. */
  snoozes: Record<string, ExceptionSnooze>;
  /** Acknowledgements by derived exception row id. */
  acks: Record<string, ExceptionAck>;

  // Actions — lines lens
  setLinesFilters: (patch: Partial<OccLinesFilters>) => void;
  resetLinesFilters: () => void;

  // Actions — exceptions lens
  setExceptionFilters: (patch: Partial<OccExceptionFilters>) => void;
  resetExceptionFilters: () => void;
  snoozeException: (id: string, reason: string, until: string, at: string) => void;
  acknowledgeException: (id: string, reason: string, at: string) => void;
  /** Drop marks whose derived row no longer exists (rule stopped firing). */
  pruneExceptionMarks: (activeIds: string[]) => void;

  reset: () => void;
}

const initialLinesFilters: OccLinesFilters = {
  search: '',
  node: 'all',
  owner: 'all',
  exceptionsOnly: false,
  balanceOnly: false,
  groupBySo: false,
};

const initialExceptionFilters: OccExceptionFilters = {
  reason: 'all',
  showSnoozed: false,
};

const initialState = {
  linesFilters: initialLinesFilters,
  exceptionFilters: initialExceptionFilters,
  snoozes: {} as Record<string, ExceptionSnooze>,
  acks: {} as Record<string, ExceptionAck>,
};

export const useOccStore = create<OccState>()(
  devtools(
    persist(
      (set) => ({
        ...initialState,

        setLinesFilters: (patch) =>
          set(
            (state) => ({ linesFilters: { ...state.linesFilters, ...patch } }),
            false,
            'occ/setLinesFilters'
          ),

        resetLinesFilters: () =>
          set({ linesFilters: { ...initialLinesFilters } }, false, 'occ/resetLinesFilters'),

        setExceptionFilters: (patch) =>
          set(
            (state) => ({ exceptionFilters: { ...state.exceptionFilters, ...patch } }),
            false,
            'occ/setExceptionFilters'
          ),

        resetExceptionFilters: () =>
          set(
            { exceptionFilters: { ...initialExceptionFilters } },
            false,
            'occ/resetExceptionFilters'
          ),

        snoozeException: (id, reason, until, at) =>
          set(
            (state) => ({ snoozes: { ...state.snoozes, [id]: { until, reason, at } } }),
            false,
            'occ/snoozeException'
          ),

        acknowledgeException: (id, reason, at) =>
          set(
            (state) => ({ acks: { ...state.acks, [id]: { at, reason } } }),
            false,
            'occ/acknowledgeException'
          ),

        pruneExceptionMarks: (activeIds) =>
          set(
            (state) => {
              const keep = new Set(activeIds);
              const prune = (marks: Record<string, ExceptionSnooze> | Record<string, ExceptionAck>) =>
                Object.fromEntries(Object.entries(marks).filter(([id]) => keep.has(id)));
              return {
                snoozes: prune(state.snoozes) as Record<string, ExceptionSnooze>,
                acks: prune(state.acks) as Record<string, ExceptionAck>,
              };
            },
            false,
            'occ/pruneExceptionMarks'
          ),

        reset: () => set(initialState, false, 'occ/reset'),
      }),
      {
        name: 'occ-storage',
        partialize: (state) => ({
          linesFilters: state.linesFilters,
          exceptionFilters: state.exceptionFilters,
          snoozes: state.snoozes,
          acks: state.acks,
        }),
      }
    ),
    { name: 'OccStore' }
  )
);
